import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import './EmailDomainSuggest.css';

export const EMAIL_SUGGEST_DOMAINS = [
  'gmail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'icloud.com',
  'live.com',
];

const parseEmail = (value = '') => {
  const at = value.indexOf('@');
  if (at < 0) {
    return { hasAt: false, local: value, domainQuery: '' };
  }
  return {
    hasAt: true,
    local: value.slice(0, at),
    domainQuery: value.slice(at + 1),
  };
};

const isKnownCompleteDomain = (domainQuery) =>
  EMAIL_SUGGEST_DOMAINS.some((domain) => domain === domainQuery.toLowerCase());

const EmailDomainSuggestInput = ({
  id,
  name = 'email',
  value = '',
  onChange,
  placeholder = 'Email Address',
  autoComplete = 'email',
  disabled = false,
  required = false,
  leftIcon = null,
  className = '',
}) => {
  const rootRef = useRef(null);
  const prevValueRef = useRef(value);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const { hasAt, local, domainQuery } = useMemo(() => parseEmail(value), [value]);
  const knownComplete = hasAt && isKnownCompleteDomain(domainQuery);

  const filtered = useMemo(() => {
    if (!hasAt) return [];
    const q = domainQuery.toLowerCase();
    return EMAIL_SUGGEST_DOMAINS.filter((domain) => domain.startsWith(q));
  }, [hasAt, domainQuery]);

  const showTrigger = hasAt && !knownComplete && !disabled;
  const showList = open && showTrigger && filtered.length > 0;

  useEffect(() => {
    const prev = prevValueRef.current ?? '';
    prevValueRef.current = value;

    const prevParsed = parseEmail(prev);
    const nextParsed = parseEmail(value);
    const known = nextParsed.hasAt && isKnownCompleteDomain(nextParsed.domainQuery);

    if (!nextParsed.hasAt || known) {
      setOpen(false);
      return;
    }

    const typedAt = nextParsed.hasAt && !prevParsed.hasAt;
    const retypedAt = value.endsWith('@') && !prev.endsWith('@');
    const editedDomain =
      nextParsed.hasAt &&
      prevParsed.hasAt &&
      nextParsed.domainQuery !== prevParsed.domainQuery;

    if (typedAt || retypedAt || editedDomain) {
      setOpen(true);
      setHighlight(0);
    }
  }, [value]);

  useEffect(() => {
    setHighlight((i) => (filtered.length ? Math.min(i, filtered.length - 1) : 0));
  }, [filtered.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const emit = (next) => {
    if (typeof onChange === 'function') onChange(next);
  };

  const applyDomain = (domain) => {
    emit(`${local}@${domain}`);
    setOpen(false);
  };

  const triggerLabel = domainQuery
    ? `@${(filtered[highlight] || filtered[0] || domainQuery).split('.')[0]}`
    : 'Select domain';

  const handleKeyDown = (e) => {
    if (!hasAt || knownComplete || disabled) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlight(0);
        return;
      }
      setHighlight((i) => (filtered.length ? (i + 1) % filtered.length : 0));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlight(filtered.length ? filtered.length - 1 : 0);
        return;
      }
      setHighlight((i) => (filtered.length ? (i - 1 + filtered.length) % filtered.length : 0));
      return;
    }

    if (e.key === 'Enter' && open && filtered[highlight]) {
      e.preventDefault();
      applyDomain(filtered[highlight]);
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`eds${leftIcon ? ' eds--icon' : ''}${showTrigger ? ' eds--trigger' : ''} ${className}`.trim()}
    >
      <div className="eds__field">
        {leftIcon ? <span className="eds__icon">{leftIcon}</span> : null}
        <input
          id={id}
          name={name}
          type="email"
          role="combobox"
          autoComplete={autoComplete}
          required={required}
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          className="eds__input"
          onChange={(e) => emit(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={id ? `${id}-domains` : undefined}
          aria-haspopup="listbox"
        />
        {showTrigger ? (
          <button
            type="button"
            className="eds__trigger"
            tabIndex={-1}
            aria-expanded={showList}
            aria-label="Select email domain"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="eds__trigger-label">{triggerLabel}</span>
            <ChevronDown className="eds__chevron" strokeWidth={2.25} />
          </button>
        ) : null}
      </div>

      {showList ? (
        <ul id={id ? `${id}-domains` : undefined} className="eds__list" role="listbox">
          {filtered.map((domain, index) => (
            <li key={domain} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={index === highlight}
                className={`eds__item${index === highlight ? ' is-active' : ''}`}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyDomain(domain)}
              >
                @{domain}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default EmailDomainSuggestInput;
